import { useMemo } from 'react';
import { useAppDispatch } from '@/app/store/store';
import { browsingHistoryBuild } from '@/features/browsing-history/config/browsingHistory.build';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { getPageName } from '@/shared/features/analytics/utils/commonAttributes';
/**
 * Independent mobile bottom bar for PLP V3/V4, selected by PlpBase.
 * The five-column translucent layout changes presentation; Sort and Filter use the existing
 * page callbacks. In Store opens the pincode popup; Cart opens /order; Viewed opens /recently-viewed.
 */
import { AppLink } from '@/shared/components/AppLink';
import { PLP_MAX_WIDTH } from '../../constants';

interface PlpBottomBarV2Props {
  pincode?: string;
  onOpenPincode?: () => void;
  onActivateStoreDesigns?: () => void;
  onOpenSort?: () => void;
  onOpenFilter?: () => void;
  hidePincode?: boolean;
  variant?: 'default' | 'store';
}

export function PlpBottomBarV2({
  pincode = '',
  onOpenPincode,
  onActivateStoreDesigns,
  onOpenSort,
  onOpenFilter,
}: PlpBottomBarV2Props) {
  const { analytics } = useAnalytics();
  const dispatch = useAppDispatch();
  const { service } = useMemo(() => browsingHistoryBuild(dispatch), [dispatch]);
  const trackRecentlyViewed = () => {
    if (!analytics) return;
    const pageAttributes = getPageName();
    // Read the actual history total without delaying navigation.
    void service.getListingPage(undefined, 1).then(result => {
      analytics.trackEvent('Recently Viewed Clicked', { ...pageAttributes, 'Item Count': result.totalCount });
    }).catch(() => {
      // A failed lookup is unknown, not an empty browsing history.
      analytics.trackEvent('Recently Viewed Clicked', pageAttributes);
    });
  };
  return (
    <nav
      aria-label="Product listing actions"
      className="fixed inset-x-0 bottom-0 z-20 overflow-hidden rounded-t-[32px] border border-b-0 border-black/10 bg-white/80 shadow-[0_-10px_28px_rgba(15,23,42,0.10)] backdrop-blur-xl md:hidden"
    >
      {/* Equal columns and 25px icon slots align differing SVG shapes; safe-area padding clears the home indicator. */}
      <div
        className={`mx-auto grid w-full max-w-[${PLP_MAX_WIDTH}px] grid-cols-5 items-stretch px-2 pb-[max(10px,env(safe-area-inset-bottom))] pt-[10px]`}
      >
        <AppLink to="/recently-viewed" onClick={trackRecentlyViewed} aria-label="Recently Viewed" className="flex min-w-0 flex-col items-center gap-1.5 text-black">
          <span className="flex h-[25px] items-center justify-center" aria-hidden>
          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" aria-hidden>
            <path d="M13.7503 8.33333H12.5003V13.5417L16.9587 16.1875L17.5003 15L13.7503 12.8125V8.33333ZM13.542 3.125C11.0556 3.125 8.67102 4.11272 6.91287 5.87087C5.15471 7.62903 4.16699 10.0136 4.16699 12.5H1.04199L5.16699 16.6979L9.37533 12.5H5.62527C5.62527 10.5661 6.44532 8.24245 7.81277 6.875C9.18023 5.50755 11.6081 4.375 13.542 4.375C15.4759 4.375 18.0078 5.82005 19.3753 7.1875C20.7427 8.55495 21.5628 10.8265 21.5628 12.7604C21.5628 14.6943 20.4302 16.7575 19.0628 18.125C17.6953 19.4925 15.4759 20.625 13.542 20.625C11.5316 20.625 9.43777 19.4479 8.12527 18.125L6.91699 19.125C7.78325 20.0005 8.81526 20.6945 9.95275 21.1667C11.0902 21.6389 12.3104 21.8797 13.542 21.875C16.0284 21.875 18.413 20.8873 20.1711 19.1291C21.9293 17.371 22.917 14.9864 22.917 12.5C22.917 10.0136 21.9293 7.62903 20.1711 5.87087C18.413 4.11272 16.0284 3.125 13.542 3.125Z" fill="currentColor" />
          </svg>
          </span>
          <span className="text-xs font-normal leading-none">Viewed</span>
        </AppLink>
        <button type="button" onClick={() => {
          analytics?.trackEvent('Designs In Store Button Clicked', { 'Pincode': pincode, 'Pincode Value': pincode }, { eventProviders: ['webengage'] });
          onOpenPincode?.();
          onActivateStoreDesigns?.();
        }} className="flex min-w-0 flex-col items-center gap-1.5 text-black">
          <span className="flex h-[25px] items-center justify-center" aria-hidden>
          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" aria-hidden>
            <path d="M20.8295 11.0635V19.7917C20.8295 20.3442 20.61 20.8741 20.2193 21.2648C19.8286 21.6555 19.2987 21.875 18.7462 21.875H6.25451C5.70216 21.8747 5.17252 21.6551 4.78205 21.2644C4.39157 20.8738 4.17222 20.344 4.17222 19.7917V11.0635M16.6649 3.125L17.1858 9.11458C17.1858 12.4229 22.5556 11.7396 21.8035 8.85625L20.7149 4.68229C20.5986 4.23649 20.3378 3.84183 19.9732 3.56005C19.6087 3.27827 19.1611 3.12528 18.7003 3.125H6.30035C5.83961 3.12528 5.39197 3.27827 5.02745 3.56005C4.66293 3.84183 4.4021 4.23649 4.28576 4.68229L3.19722 8.85625C2.44514 11.7406 7.81493 12.4229 7.81493 9.11458M17.1858 9.11458C17.1858 12.1375 12.5003 12.1375 12.5003 9.11458M12.5003 9.11458C12.5003 12.1375 7.81493 12.1375 7.81493 9.11458L8.33576 3.125M12.5003 9.11458V3.125" stroke="currentColor" strokeWidth="1.21951" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          </span>
          <span className="whitespace-nowrap text-xs font-normal leading-none">In Store</span>
        </button>
        <AppLink
          to="/order"
          shouldRefresh
          onClick={() => analytics?.trackEvent('Cart Icon Clicked', getPageName())}
          className="flex min-w-0 flex-col items-center gap-1.5 text-black"
        >
          <span className="flex h-[25px] items-center justify-center" aria-hidden>
          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" aria-hidden>
            <path d="M2.49805 3.21024C2.49805 3.02084 2.57329 2.83919 2.70722 2.70526C2.84114 2.57133 3.02279 2.49609 3.21219 2.49609H3.83779C4.87616 2.49609 5.47319 3.17168 5.81883 3.84583C6.0545 4.30717 6.22447 4.86992 6.36587 5.3584H21.0702C21.2909 5.35846 21.5086 5.40967 21.7061 5.508C21.9037 5.60633 22.0758 5.74911 22.2089 5.92514C22.3421 6.10116 22.4326 6.30564 22.4734 6.52253C22.5142 6.73942 22.5042 6.96282 22.4442 7.17519L20.3075 14.7094C20.1375 15.3072 19.7772 15.8332 19.2813 16.2077C18.7854 16.5823 18.1809 16.7848 17.5594 16.7848H10.3051C9.67848 16.785 9.06917 16.5791 8.57104 16.199C8.07292 15.8188 7.71362 15.2854 7.5485 14.6809L6.63153 11.3172L6.61725 11.2715L5.14611 6.27536L5.00328 5.79403C4.86045 5.29984 4.73476 4.86278 4.54622 4.49571C4.31912 4.05294 4.11059 3.92439 3.83779 3.92439H3.21219C3.02279 3.92439 2.84114 3.84915 2.70722 3.71522C2.57329 3.58129 2.49805 3.39965 2.49805 3.21024ZM7.99413 10.8859L8.92681 14.3081C9.0095 14.6102 9.1892 14.8768 9.43825 15.0667C9.6873 15.2567 9.99189 15.3595 10.3051 15.3593H17.5609C17.8714 15.359 18.1734 15.2575 18.4211 15.0701C18.6688 14.8827 18.8487 14.6197 18.9334 14.321L21.0702 6.78669H6.78722L7.99413 10.8859ZM12.5004 20.3541C12.5004 20.9223 12.2747 21.4672 11.8729 21.869C11.4711 22.2708 10.9262 22.4965 10.358 22.4965C9.78975 22.4965 9.24481 22.2708 8.84302 21.869C8.44124 21.4672 8.21552 20.9223 8.21552 20.3541C8.21552 19.7859 8.44124 19.2409 8.84302 18.8391C9.24481 18.4374 9.78975 18.2116 10.358 18.2116C10.9262 18.2116 11.4711 18.4374 11.8729 18.8391C12.2747 19.2409 12.5004 19.7859 12.5004 20.3541ZM11.0721 20.3541C11.0721 20.1647 10.9969 19.983 10.8629 19.8491C10.729 19.7152 10.5474 19.6399 10.358 19.6399C10.1686 19.6399 9.98691 19.7152 9.85298 19.8491C9.71905 19.983 9.64381 20.1647 9.64381 20.3541C9.64381 20.5435 9.71905 20.7251 9.85298 20.8591C9.98691 20.993 10.1686 21.0682 10.358 21.0682C10.5474 21.0682 10.729 20.993 10.8629 20.8591C10.9969 20.7251 11.0721 20.5435 11.0721 20.3541ZM19.6419 20.3541C19.6419 20.9223 19.4162 21.4672 19.0144 21.869C18.6126 22.2708 18.0676 22.4965 17.4994 22.4965C16.9312 22.4965 16.3863 22.2708 15.9845 21.869C15.5827 21.4672 15.357 20.9223 15.357 20.3541C15.357 19.7859 15.5827 19.2409 15.9845 18.8391C16.3863 18.4374 16.9312 18.2116 17.4994 18.2116C18.0676 18.2116 18.6126 18.4374 19.0144 18.8391C19.4162 19.2409 19.6419 19.7859 19.6419 20.3541ZM18.2136 20.3541C18.2136 20.1647 18.1383 19.983 18.0044 19.8491C17.8705 19.7152 17.6888 19.6399 17.4994 19.6399C17.31 19.6399 17.1284 19.7152 16.9945 19.8491C16.8605 19.983 16.7853 20.1647 16.7853 20.3541C16.7853 20.5435 16.8605 20.7251 16.9945 20.8591C17.1284 20.993 17.31 21.0682 17.4994 21.0682C17.6888 21.0682 17.8705 20.993 18.0044 20.8591C18.1383 20.7251 18.2136 20.5435 18.2136 20.3541Z" fill="currentColor" />
          </svg>
          </span>
          <span className="text-xs font-normal leading-none">Cart</span>
        </AppLink>
        <button
          type="button"
          className="flex min-w-0 flex-col items-center gap-1.5 text-black"
          onClick={onOpenSort}
        >
          <span className="flex h-[25px] items-center justify-center" aria-hidden>
          <svg
            viewBox="0 0 24 24"
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M3 6h18" />
            <path d="M7 12h10" />
            <path d="M10 18h4" />
          </svg>
          </span>
          <span className="text-xs font-normal leading-none">Sort</span>
        </button>
        <button
          type="button"
          className="flex min-w-0 flex-col items-center gap-1.5 text-black"
          onClick={onOpenFilter}
        >
          <span className="flex h-[25px] items-center justify-center" aria-hidden>
          <svg
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.21951"
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M2.69238 8.94271V0.609375M17.2757 17.276V14.151M2.69238 17.276V13.1094M17.2757 9.98437V0.609375M9.98405 3.73437V0.609375M9.98405 17.276V7.90104" />
            <path d="M2.69271 13.11C3.8433 13.11 4.77604 12.1773 4.77604 11.0267C4.77604 9.8761 3.8433 8.94336 2.69271 8.94336C1.54212 8.94336 0.609375 9.8761 0.609375 11.0267C0.609375 12.1773 1.54212 13.11 2.69271 13.11Z" />
            <path d="M9.9847 7.90104C11.1353 7.90104 12.068 6.9683 12.068 5.81771C12.068 4.66712 11.1353 3.73438 9.9847 3.73438C8.83411 3.73438 7.90137 4.66712 7.90137 5.81771C7.90137 6.9683 8.83411 7.90104 9.9847 7.90104Z" />
            <path d="M17.2757 14.151C18.4263 14.151 19.359 13.2183 19.359 12.0677C19.359 10.9171 18.4263 9.98438 17.2757 9.98438C16.1251 9.98438 15.1924 10.9171 15.1924 12.0677C15.1924 13.2183 16.1251 14.151 17.2757 14.151Z" />
          </svg>
          </span>
          <span className="text-xs font-normal leading-none">Filter</span>
        </button>
      </div>
    </nav>
  );
}
